/**
 * 「最大電力補正」モーダル (design `#dft-rescale-modal`。共通仕様
 * demand-forecast-common.md AC-DFC-EDIT-001。機能名は「最大電力で揃える」から
 * 「最大電力補正」に変更、2026-07-09 調整会)。
 *
 * 現在の48点ピーク需要を表示し、目標最大電力 (kWh、正の数。小数も可〈AC-DFC-EDIT-003〉。
 * demand-fix-48-koma で MW 表示から kWh 統一) を入力すると現在の需要想定値を目標値に合わせて
 * 比例配分する (受給済みコマは変更しない)。保存値も送電端 kWh/30min
 * (AC-DFC-011) なので、呼び出し側 (index.tsx) は入力値をそのまま
 * `rescaleSlotsToPeak` へ渡す (Q3c/Q8)。
 *
 * ## design (8080) との対応 (2026-08-28 デザイナー指摘「8080 に準拠」)
 *
 * 補正前後を**左右に並べ**、適用前に効き目 (倍率/差分) が読めるようにする
 * (design の `.rescale-compare` / `.rescale-delta`)。左 (現在) は読取専用なので
 * 入力欄ではなく静的値で置く。
 *
 * **[design 差異] 単位は kWh のまま**とする。design プロトは「MW」表記だが、
 * spec AC-DFC-011 が「コマ値・チャートY軸・**ピーク需要 (最大電力補正の目標値を
 * 含む)** は同一の kWh スケールでそろえ、画面内で単位が混在しないようにする」と
 * 明示しており、ここだけ MW に戻すと隣の 48 コマ表と単位が食い違う。
 * design プロト側を kWh へ直す (デザイナー確認済み)。
 */
import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  HStack,
  Input,
  PrimaryButton,
  Stack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowRightRegular } from '@enechain/ecloud-icons'

import { Modal } from '~/components/common/Modal'
import { iconSize } from '~/constants/iconSize'
import { formatKwh } from '~/models/kwhDisplay'
import { filterNumericImeInput } from '~/pages/DemandForecastDaily/models/numericInputFilter'

/**
 * 「現在の最大電力」の静的値と入力欄の行の高さ。
 *
 * design の `.ecl-input--sm` は名前に反して **40px** (`components.css:3207`) で、
 * DS の既定 (`md`) と同値。左右の行頭を揃えるため静的値側にも同じ高さを与える
 * (design CSS の `.rescale-current-val` は 32px のままだが、これは input が
 * 32px だった頃の値が残っているもので、同 CSS 内で入力欄と 8px 食い違う)。
 */
const VALUE_ROW_HEIGHT = '40px'

type Props = {
  isOpen: boolean
  /** 現在の48点ピーク需要 (kWh。保持値のまま渡し、表示時だけ整数に丸める) */
  currentPeakKwh: number
  /**
   * 受給済み (GC 済み) コマ数。0 より大きい時だけ「変更されないコマがある」旨を
   * Alert で出す (AC-DFC-EDIT-001 ※「締切済みコマは変更対象外」の可視化)。
   */
  lockedKomaCount: number
  onApply: (targetPeakKwh: number) => void
  onCancel: () => void
}

/**
 * 倍率を design と同じ桁で表す (`×1.05` / `×1.5` / `×2`)。
 * 小数第3位まで出し、末尾の 0 と余った小数点を落とす。
 */
const formatFactor = (factor: number): string =>
  factor.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')

export const RescaleModal = ({
  isOpen,
  currentPeakKwh,
  lockedKomaCount,
  onApply,
  onCancel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [input, setInput] = useState('')

  // ダイアログを開くたびに入力をリセットする (ConfirmDemandDialog と同パターン)
  useEffect(() => {
    if (isOpen) setInput('')
  }, [isOpen])

  const parsed = parseFloat(input)
  // AC-DFC-EDIT-003: 正の数 (小数を含む) を受け付ける
  const isValid = input.trim() !== '' && !isNaN(parsed) && parsed > 0

  // 倍率/差分は「適用したら何が起きるか」の予告。差が整数表示で 0 になる時は
  // 「×1 ±0」を出しても情報がないので出さない (design の `_dftRescaleSyncDelta` と同じ)。
  const diffKwh = isValid ? parsed - currentPeakKwh : 0
  const showDelta =
    isValid && currentPeakKwh > 0 && formatKwh(Math.abs(diffKwh)) !== '0'

  const handleApply = (): void => {
    if (!isValid) return
    onApply(parsed)
  }

  const footer = (
    <HStack gap={3}>
      <TertiaryButton onClick={onCancel}>
        {t('common:action.cancel')}
      </TertiaryButton>
      <PrimaryButton disabled={!isValid} onClick={handleApply}>
        {t('page:demandForecastDaily.rescale.applyButton')}
      </PrimaryButton>
    </HStack>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onCancel}
      size="sm"
      title={t('page:demandForecastDaily.rescale.title')}
    >
      <Stack gap={3}>
        {/* 補正前 → 補正後 を左右に並べる (design `.rescale-compare`)。
            `align="flex-end"` で、ラベルの行数に関わらず値/入力/矢印の行が
            必ず揃う (design は矢印に margin-top を積んで揃えているが、
            ラベルの折返しが増えると崩れるため下端合わせにする)。 */}
        <HStack align="flex-end" gap={3}>
          <Stack flex="1 1 0" gap={1} minWidth={0}>
            <Text textStyle="body" color="Text.midEmphasis">
              {t('page:demandForecastDaily.rescale.currentPeak')}
            </Text>
            <Flex alignItems="center" height={VALUE_ROW_HEIGHT}>
              <Text
                textStyle="readoutValue"
                color="Text.highEmphasis"
                fontVariantNumeric="tabular-nums"
              >
                {t('page:demandForecastDaily.rescale.kwhValue', {
                  value: formatKwh(currentPeakKwh),
                })}
              </Text>
            </Flex>
          </Stack>
          <Flex
            alignItems="center"
            flexShrink={0}
            height={VALUE_ROW_HEIGHT}
            color="Text.lowEmphasis"
          >
            <FaArrowRightRegular boxSize={iconSize.labeledButton} aria-hidden />
          </Flex>
          <Stack flex="1 1 0" gap={1} minWidth={0}>
            <Text textStyle="body" color="Text.midEmphasis">
              {t('page:demandForecastDaily.rescale.newPeakLabel')}
            </Text>
            <Input
              aria-label={t('page:demandForecastDaily.rescale.newPeakLabel')}
              inputMode="decimal"
              min={0}
              onChange={(e) => {
                // design note demand-disable-ime Decision 1/2: IME 由来の全角/かな
                // 入力を入力段階で除去する (正の数チェックは isValid に委ねる)。
                setInput(filterNumericImeInput(e.target.value))
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleApply()
                }
              }}
              step="any"
              type="number"
              value={input}
            />
          </Stack>
        </HStack>

        {/* 倍率 + 差分 (入力に追従)。増減自体に良し悪しは無いので positive /
            negative の色は使わず中立色に保つ (design `.rescale-delta`)。
            出たり消えたりで下の説明文が動かないよう高さを確保する。 */}
        <Text
          textStyle="body"
          minHeight="1.4em"
          color="Text.midEmphasis"
          aria-live="polite"
          fontVariantNumeric="tabular-nums"
        >
          {showDelta && (
            <>
              <Text as="span" color="Text.highEmphasis" fontWeight="bold">
                {t('page:demandForecastDaily.rescale.deltaValue', {
                  factor: formatFactor(parsed / currentPeakKwh),
                  diff: `${diffKwh > 0 ? '+' : '-'}${formatKwh(Math.abs(diffKwh))}`,
                })}
              </Text>{' '}
              {t('page:demandForecastDaily.rescale.deltaSuffix')}
            </>
          )}
        </Text>

        <Text textStyle="body" color="Text.midEmphasis">
          {t('page:demandForecastDaily.rescale.description')}
        </Text>

        {/* 受給済みコマが実在する時だけ「何が変更されないか」を状態情報として出す
            (常時の説明は上の description)。design `#dft-rescale-locked-alert`。 */}
        {lockedKomaCount > 0 && (
          <Alert
            status="info"
            title={t('page:demandForecastDaily.rescale.lockedKomaNotice', {
              count: lockedKomaCount,
            })}
          />
        )}
      </Stack>
    </Modal>
  )
}
